import { Dashboard } from '@/common/dashboard/Dashboard';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { DimRearrangeButton } from '@/common/entityFramework/entityPerspective/support/DimRearrangeButton';
import { DropdownSelect } from '@/common/form/formFields/DropdownSelect';
import { EntitySelect } from '@/common/form/formFields/EntitySelect';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useMemo, useState } from 'react';
import { McoInvTrendCard } from './McoInvTrendCard';

const metricOptions = [
  { value: 'RBT', displayName: 'Rebate Amount' },
  { value: 'PYMT', displayName: 'Payment Amount' },
  { value: 'DUE', displayName: 'Due Amount' },
  { value: 'QTY', displayName: 'Quantity' },
];

// All Entity Filter Field Definitions
const entityFilterFieldDefs = [
  {
    field: 'rbtPeriodExpr',
    displayName: 'Rebate Periods',
    entity: 'PERIOD',
    code: 'RBT_PERIOD',
    module: 'sc',
    multiple: true,
    expr: ExprUtils.whereParams('periodicity', 'MON'),
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCO_INV_LINE',
    parentFilterFkField: 'rbtPeriodId',
    hidden: true,
  },
  {
    field: 'earnedPeriodExpr',
    displayName: 'Earned Periods',
    entity: 'PERIOD',
    code: 'EARNED_PERIOD',
    module: 'sc',
    multiple: true,
    expr: ExprUtils.whereParams('periodicity', 'MON'),
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCO_INV_LINE',
    parentFilterFkField: 'earnedPeriodId',
  },
  {
    field: 'prodExpr',
    displayName: 'Products',
    entity: 'NDC11_PROD',
    code: 'NDC11_PROD',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCO_INV_LINE',
    parentFilterFkField: 'prodId',
  },
  {
    field: 'planCustExpr',
    displayName: 'Plans',
    entity: 'CUST',
    code: 'PLAN_CUST',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCO_INV_LINE',
    parentFilterFkField: 'planCustId',
  },
  {
    field: 'ctrtExpr',
    displayName: 'Contracts',
    entity: 'CTRT',
    code: 'CTRT',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCO_INV',
    parentFilterFkField: 'ctrtId',
  },
  {
    field: 'pbmExpr',
    displayName: 'PBMs',
    entity: 'CUST',
    code: 'PBM_CUST',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCO_INV',
    parentFilterFkField: 'pbmId',
  },
];

// X-axis options for the dashboard charts
const xAxisOptions = [
  ...entityFilterFieldDefs.map((fieldDef) => ({ code: fieldDef.code, displayName: fieldDef.displayName, fieldDef })),
];

// All filters
const codeToFilter = Object.fromEntries(
  entityFilterFieldDefs.map((fieldDef) => [fieldDef.code, { displayName: fieldDef.displayName }]),
);

// Default filters
const defaultVisibleFilters = entityFilterFieldDefs
  .filter((fieldDef) => !fieldDef.hidden)
  .map((fieldDef) => fieldDef.code);

export const McoInvFinDashboard = () => {
  const rsrc = useRsrc();

  const [defaultFormValues, setDefaultFormValues] = useState(null);
  const [visibleFilters, setVisibleFilters] = useNavStateSync('visibleFilters', defaultVisibleFilters);

  useEffect(() => {
    rsrc.data('sc', 'MCO_INV_DASHBOARD_FILTERS_DATA_PLUGIN', {}).then((res) => {
      setDefaultFormValues({
        metric: ['PYMT'],
        prodExpr: null,
        planCustExpr: null,
        earnedPeriodExpr: ExprUtils.whereParams('id', res.earnedPeriodIds),
        earnedPeriodExprSelectedCount: res.earnedPeriodIds.length,
        earnedPeriodExprSelectedIds: res.earnedPeriodIds,
        earnedPeriodExprHeaderChecked: false,
        rbtPeriodExpr: null,
        ctrtExpr: null,
        pbmExpr: null,
        xAxis: 'EARNED_PERIOD',
      });
    });
  }, []);

  // Saved nav state may reference filters that no longer exist
  const activeFieldDefs = useMemo(
    () =>
      visibleFilters.map((code) => entityFilterFieldDefs.find((fieldDef) => fieldDef.code === code)).filter(Boolean),
    [visibleFilters],
  );

  const resetDims = () => {
    setVisibleFilters(defaultVisibleFilters);
  };

  if (!defaultFormValues) return null;

  const augButtons = (buttons) => {
    buttons.unshift(
      <DimRearrangeButton
        key="filterRearrangeButton"
        setDims={setVisibleFilters}
        codeToDim={codeToFilter}
        activeDims={activeFieldDefs.map(({ code }) => ({ code }))}
      />,
    );
  };

  return (
    <Dashboard
      title="Managed Care Financial Dashboard"
      titleIcon={fas.faFileInvoice}
      entity="MCO_INV"
      defaultFormValues={defaultFormValues}
      augButtons={augButtons}
      onReset={resetDims}
    >
      <FilterContainer>
        <DropdownSelect
          fieldDef={{
            field: 'metric',
            displayName: 'Metric',
            multiSelect: true,
            values: metricOptions,
          }}
        />
        {activeFieldDefs.map((fieldDef) => (
          <EntitySelect key={fieldDef.field} fieldDef={{ ...fieldDef, variant: 'outlined' }} />
        ))}
      </FilterContainer>
      <SpacerLayout>
        <Spacer weights={[2, 1]}>
          <McoInvTrendCard xAxisOptions={xAxisOptions} metricOptions={metricOptions} />
        </Spacer>
      </SpacerLayout>
    </Dashboard>
  );
};
